import { useTranslation } from "react-i18next"

import type { ShareFilter } from "@/features/sharing/utils/share-filter"

import { SHARE_FILTERS } from "@/features/sharing/utils/share-filter"
import { cn } from "@/lib/utils"

type ShareFilterRowProps = {
  value: ShareFilter
  onChange: (filter: ShareFilter) => void
  /** Prefix for per-button data-testids, e.g. "projects-share-filter" → "projects-share-filter-with_me". */
  testIdPrefix?: string
}

/**
 * The sidebar "All | Shared: With me · By me" pill row, shared between the
 * Chats and Projects sections so both surfaces stay visually identical.
 */
export function ShareFilterRow({ value, onChange, testIdPrefix }: ShareFilterRowProps) {
  const { t } = useTranslation()

  const label = (filter: ShareFilter) => {
    switch (filter) {
      case "with_me":
        return t("sharing.filter_with_me", { defaultValue: "With me" })
      case "by_me":
        return t("sharing.filter_by_me", { defaultValue: "By me" })
      default:
        return t("sharing.filter_all", { defaultValue: "All" })
    }
  }

  const buttonBase =
    "rounded-md px-1.5 py-0.5 text-xs whitespace-nowrap transition-colors duration-150"
  const renderButton = (filter: ShareFilter) => (
    <button
      key={filter}
      aria-pressed={value === filter}
      data-testid={testIdPrefix ? `${testIdPrefix}-${filter}` : undefined}
      type="button"
      className={cn(
        buttonBase,
        value === filter
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-muted-foreground hover:text-sidebar-foreground"
      )}
      onClick={() => onChange(filter)}
    >
      {label(filter)}
    </button>
  )

  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 px-2 pt-0.5 pb-2.5">
      {renderButton("all")}
      <span className="text-muted-foreground/70 ml-0.5 cursor-default text-xs whitespace-nowrap select-none">
        {t("sharing.filter_shared_prefix", { defaultValue: "Shared" })}:
      </span>
      {SHARE_FILTERS.filter((f) => f !== "all").map((f) => renderButton(f))}
    </div>
  )
}
